import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Table from '@cloudscape-design/components/table';
import Header from '@cloudscape-design/components/header';
import ContentLayout from '@cloudscape-design/components/content-layout';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Select, { SelectProps } from '@cloudscape-design/components/select';
import FormField from '@cloudscape-design/components/form-field';
import Alert from '@cloudscape-design/components/alert';
import Container from '@cloudscape-design/components/container';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import { livekitService, ScenarioInfo } from '../services/livekit';
import { getIdToken } from '../services/auth';

/** Option value representing all industries (no filter) */
const ALL_INDUSTRIES_VALUE = '__all__';

/**
 * Scenario Selection Page - Browse and select training scenarios
 *
 * Displays a Cloudscape Table with available training scenarios.
 * Users can click on a scenario row to navigate to the voice session page.
 */
export function ScenarioSelectionPage(): React.ReactElement {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scenarios, setScenarios] = useState<ScenarioInfo[]>([]);
  const [selectedScenarios, setSelectedScenarios] = useState<ScenarioInfo[]>([]);
  const [selectedIndustry, setSelectedIndustry] = useState<SelectProps.Option | null>(null);

  // Load scenarios on mount
  useEffect(() => {
    const loadScenarios = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await livekitService.getScenarios();
        setScenarios(data);
      } catch (err) {
        console.error('Failed to load scenarios:', err);
        setError('Failed to load scenarios. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadScenarios();
  }, []);

  /**
   * Extract unique industries from scenarios and create dropdown options.
   */
  const industryOptions: SelectProps.Options = useMemo(() => {
    const uniqueIndustries = [...new Set(scenarios.map((s) => s.industry))].sort();
    const industryOpts: SelectProps.Option[] = uniqueIndustries.map((industry) => ({
      label: industry,
      value: industry,
    }));
    
    return [
      { label: 'All Industries', value: ALL_INDUSTRIES_VALUE },
      ...industryOpts,
    ];
  }, [scenarios]);

  /**
   * Filter scenarios based on selected industry.
   */
  const filteredScenarios = useMemo(() => {
    if (!selectedIndustry || selectedIndustry.value === ALL_INDUSTRIES_VALUE) {
      return scenarios;
    }
    return scenarios.filter((scenario) => scenario.industry === selectedIndustry.value);
  }, [scenarios, selectedIndustry]);

  const handleIndustryChange = (event: { detail: SelectProps.ChangeDetail }): void => {
    setSelectedIndustry(event.detail.selectedOption);
  };

  /**
   * Handle starting a session with the selected scenario.
   */
  const handleStartSession = (): void => {
    if (selectedScenarios.length === 0) return;
    
    const scenario = selectedScenarios[0];
    navigate(`/session/${scenario.id}`, {
      state: {
        scenarioId: scenario.id,
        scenarioName: scenario.name,
        role: scenario.role,
        industry: scenario.industry,
        description: scenario.description
      }
    });
  };

  /**
   * Handle viewing the full case study brief in a new tab.
   * Fetches the HTML from the authenticated API and opens it.
   */
  const handleViewBrief = useCallback(async (): Promise<void> => {
    if (selectedScenarios.length === 0) return;

    const scenarioId = selectedScenarios[0].id;
    const apiBase = process.env.REACT_APP_API_BASE_URL || '';

    try {
      const token = await getIdToken();
      const response = await fetch(`${apiBase}/scenarios/${scenarioId}/brief`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        // Fallback: open static file (works locally without API)
        window.open(`/scenario-briefs/${scenarioId}.html`, '_blank');
        return;
      }

      const html = await response.text();
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {
      // Fallback for local dev without API
      window.open(`/scenario-briefs/${scenarioId}.html`, '_blank');
    }
  }, [selectedScenarios]);

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description="Choose a training scenario to begin your session"
        >
          Scenario Selection
        </Header>
      }
    >
      <SpaceBetween size="l">
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        <Table
          columnDefinitions={[
            {
              id: 'name',
              header: 'Scenario Name',
              cell: (item: ScenarioInfo) => item.name,
              sortingField: 'name',
              width: 300,
            },
            {
              id: 'role',
              header: 'Role',
              cell: (item: ScenarioInfo) => item.role,
              sortingField: 'role',
              width: 200,
            },
            {
              id: 'industry',
              header: 'Industry',
              cell: (item: ScenarioInfo) => item.industry,
              sortingField: 'industry',
              width: 150,
            },
            {
              id: 'description',
              header: 'Description',
              cell: (item: ScenarioInfo) => item.description,
              width: 400,
            },
          ]}
          items={filteredScenarios}
          loading={loading}
          loadingText="Loading scenarios..."
          selectionType="single"
          selectedItems={selectedScenarios}
          onSelectionChange={({ detail }) => setSelectedScenarios(detail.selectedItems)}
          trackBy="id"
          variant="full-page"
          stickyHeader={true}
          header={
            <Header
              variant="h2"
              description="Select a scenario and click Start Session"
              counter={`(${filteredScenarios.length})`}
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <FormField label="Filter by Industry">
                    <Select
                      selectedOption={selectedIndustry}
                      onChange={handleIndustryChange}
                      options={industryOptions}
                      placeholder="All Industries"
                      selectedAriaLabel="Selected"
                    />
                  </FormField>
                  <FormField label="&nbsp;">
                    <Button
                      variant="primary"
                      disabled={selectedScenarios.length === 0}
                      onClick={handleStartSession}
                    >
                      Start Session
                    </Button>
                  </FormField>
                </SpaceBetween>
              }
            >
              Available Scenarios
            </Header>
          }
          empty={
            <Box
              margin={{ vertical: 'xs' }}
              textAlign="center"
              color="inherit"
            >
              <SpaceBetween size="m">
                <b>No scenarios available</b>
                <Box variant="p" color="inherit">
                  {selectedIndustry && selectedIndustry.value !== ALL_INDUSTRIES_VALUE
                    ? `No scenarios found for industry "${selectedIndustry.label}".`
                    : 'There are no training scenarios available at this time.'}
                </Box>
              </SpaceBetween>
            </Box>
          }
        />

        {/* Scenario Brief Panel - shown when a scenario is selected */}
        {selectedScenarios.length > 0 && (
          <Container
            header={
              <Header
                variant="h2"
                description="Review the scenario context before starting your session"
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button
                      iconName="external"
                      onClick={handleViewBrief}
                      variant="normal"
                    >
                      View full case study
                    </Button>
                    <Button
                      iconName="external"
                      href="https://w.amazon.com/bin/view/AWS/AWS_SA_US-East/sa-biz-acumen/"
                      target="_blank"
                      variant="link"
                    >
                      Program wiki
                    </Button>
                  </SpaceBetween>
                }
              >
                Scenario Brief: {selectedScenarios[0].name}
              </Header>
            }
          >
            <ColumnLayout columns={2} variant="text-grid">
              <SpaceBetween size="s">
                <Box variant="h4">Meeting Context</Box>
                <Box variant="p">
                  You are an AWS Solutions Architect meeting with the <strong>{selectedScenarios[0].role}</strong> at{' '}
                  <strong>{selectedScenarios[0].name}</strong>.
                </Box>
                <Box variant="p">{selectedScenarios[0].description}</Box>
                <Box variant="small" color="text-body-secondary">
                  <strong>Industry:</strong> {selectedScenarios[0].industry} &bull;{' '}
                  <strong>Duration:</strong> ~10 minutes
                </Box>
              </SpaceBetween>
              <SpaceBetween size="s">
                <Box variant="h4">Preparation Tips</Box>
                <Box variant="p">
                  Lead the conversation with discovery questions. Understand the business
                  challenges before jumping to solutions. Listen for financial impact,
                  operational pain points, and strategic priorities.
                </Box>
                <SpaceBetween size="xs">
                  <Box variant="small"><strong>1.</strong> Introduce yourself and set the tone</Box>
                  <Box variant="small"><strong>2.</strong> Ask about their business and current challenges</Box>
                  <Box variant="small"><strong>3.</strong> Dig into financials and operational details</Box>
                  <Box variant="small"><strong>4.</strong> Explore technology landscape and constraints</Box>
                  <Box variant="small"><strong>5.</strong> Propose relevant solutions tied to business outcomes</Box>
                </SpaceBetween>
              </SpaceBetween>
            </ColumnLayout>
          </Container>
        )}
      </SpaceBetween>
    </ContentLayout>
  );
}

export default ScenarioSelectionPage;
